import { ActionIcon, Button, Divider, Group, Stack, Text, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconDownload, IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { zodResolver } from "mantine-form-zod-resolver";
import { useState } from "react";
import { useAddUserAddress, useEditUserAddress, useRemoveUserAddress } from "shared/graphql/customer";
import { userQueryOptions } from "shared/graphql/users";
import { useUserId } from "shared/stores/oidc";
import { logger } from "shared/utils/logger";
import { z } from "zod";

const locationSchema = z.object({
    name: z.string().nonempty(),
    street: z.string().nonempty(),
    city: z.string().nonempty(),
    state: z.string().nonempty(),
    zip: z.string().nonempty(),
});

const AddressDetails = () => {
    const userInfo = useUserId();
    const { data: addresses } = useSuspenseQuery({ ...userQueryOptions(userInfo), select: (data) => data?.user.addresses });
    const [showEditForm, setShowEditForm] = useState<string[]>([]);

    return addresses?.map((a) => {
        return showEditForm.includes(a.id) ? (
            <EditLocationForm key={a.id} addressId={a.id} onClose={() => setShowEditForm(showEditForm.filter((it) => it != a.id))} />
        ) : (
            <Stack key={a.id}>
                <Group justify="space-between">
                    <div>
                        <Group gap={0}>
                            <Title order={4} fz="lg">
                                {a.name}
                            </Title>

                            <ActionIcon
                                variant="transparent"
                                color="text"
                                onClick={() => setShowEditForm(showEditForm.concat(a.id))}
                                aria-label="Edit address"
                            >
                                <IconEdit size={16} />
                            </ActionIcon>
                        </Group>
                        <Text size="md">{`${a.street}, ${a.city}, ${a.state} ${a.zip}`}</Text>
                    </div>
                </Group>
                <Divider />
            </Stack>
        );
    });
};

const EditLocationForm = ({ onClose, addressId }: { addressId: string; onClose: () => void }) => {
    const userInfo = useUserId();
    const { data: addresses } = useSuspenseQuery({ ...userQueryOptions(userInfo), select: (data) => data?.user.addresses });

    const location = addresses?.find((a) => a.id == addressId)!;
    const form = useForm({
        mode: "uncontrolled",
        initialValues: {
            name: location.name,
            street: location.street,
            street2: location.street2,
            city: location.city,
            state: location.state,
            zip: location.zip,
        },
        validate: zodResolver(locationSchema),
    });

    const { editUserAddressAsync } = useEditUserAddress();
    const { removeUserAddressAsync } = useRemoveUserAddress();

    return (
        <form
            onSubmit={form.onSubmit((data, e) => {
                e?.stopPropagation();
                editUserAddressAsync({
                    userId: userInfo.userId,
                    addressId,
                    address: {
                        name: data.name,
                        street: data.street,
                        street2: data.street2,
                        city: data.city,
                        state: data.state,
                        zip: data.zip,
                    },
                })
                    .then(onClose)
                    .catch(logger.error);
            })}
        >
            <Stack>
                <TextInput
                    label="Address Nickname (Home, Work, etc.)"
                    placeholder="Enter nickname..."
                    required
                    key={form.key("name")}
                    {...form.getInputProps("name")}
                />
                <TextInput
                    label="Address"
                    placeholder="Enter address..."
                    required
                    key={form.key("street")}
                    {...form.getInputProps("street")}
                />
                <TextInput
                    label="Address Line 2"
                    placeholder="Enter address..."
                    key={form.key("street2")}
                    {...form.getInputProps("street2")}
                />
                <Group wrap="nowrap">
                    <TextInput
                        label="City"
                        placeholder="Enter city..."
                        required
                        flex={1}
                        key={form.key("city")}
                        {...form.getInputProps("city")}
                    />
                    <TextInput
                        label="State"
                        placeholder="Enter state..."
                        required
                        flex={1}
                        key={form.key("state")}
                        {...form.getInputProps("state")}
                    />
                    <TextInput
                        label="Zip"
                        placeholder="Enter zip..."
                        required
                        flex={1}
                        key={form.key("zip")}
                        {...form.getInputProps("zip")}
                    />
                </Group>
                <Group justify="flex-end">
                    <Button
                        fullWidth
                        leftSection={<IconTrash size={16} />}
                        color="red"
                        onClick={() => void removeUserAddressAsync({ userId: userInfo.userId, addressId: addressId }).then(onClose)}
                    >
                        Remove
                    </Button>
                    <Button variant="default" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button fullWidth leftSection={<IconPlus size={16} />} type="submit" color="green" disabled={!form.isDirty()}>
                        Update
                    </Button>
                </Group>
                <Divider />
            </Stack>
        </form>
    );
};

const AddLocationForm = ({ onClose }: { onClose: () => void }) => {
    const { userId } = useUserId();
    const { addUserAddressAsync } = useAddUserAddress();

    const form = useForm({
        mode: "uncontrolled",
        initialValues: { name: "Home", street: "", street2: "", city: "", state: "", zip: "" },
        validate: zodResolver(locationSchema),
    });

    return (
        <form
            onSubmit={form.onSubmit((data, e) => {
                e?.stopPropagation();
                addUserAddressAsync({
                    userId,
                    address: {
                        name: data.name,
                        street: data.street,
                        street2: data.street2,
                        city: data.city,
                        state: data.state,
                        zip: data.zip,
                    },
                })
                    .then(onClose)
                    .catch(logger.error);
            })}
        >
            <Stack>
                <TextInput
                    label="Address Nickname (Home, Work, etc.)"
                    placeholder="Enter nickname..."
                    required
                    key={form.key("name")}
                    {...form.getInputProps("name")}
                />
                <TextInput
                    label="Address"
                    placeholder="Enter address..."
                    required
                    key={form.key("street")}
                    {...form.getInputProps("street")}
                />
                <TextInput
                    label="Address Line 2"
                    placeholder="Enter address..."
                    key={form.key("street2")}
                    {...form.getInputProps("street2")}
                />
                <Group wrap="nowrap">
                    <TextInput
                        label="City"
                        placeholder="Enter city..."
                        required
                        key={form.key("city")}
                        flex={1}
                        {...form.getInputProps("city")}
                    />
                    <TextInput
                        label="State"
                        placeholder="Enter state..."
                        required
                        key={form.key("state")}
                        flex={1}
                        {...form.getInputProps("state")}
                    />
                    <TextInput
                        label="Zip"
                        placeholder="Enter zip..."
                        required
                        flex={1}
                        key={form.key("zip")}
                        {...form.getInputProps("zip")}
                    />
                </Group>
                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose} type="button">
                        Cancel
                    </Button>
                    <Button fullWidth leftSection={<IconDownload size={16} />} type="submit">
                        Add
                    </Button>
                </Group>
                <Divider />
            </Stack>
        </form>
    );
};

export const LocationsContainer = () => {
    const userInfo = useUserId();
    const { data: addresses } = useSuspenseQuery({ ...userQueryOptions(userInfo), select: (data) => data?.user.addresses });

    const [showAddForm, setShowAddForm] = useState((addresses?.length ?? 0) == 0);

    return (
        <Stack>
            <Text>Add in up to 5 addresses to receive notifications pertaining to the address locations.</Text>
            <Text fz="sm" fs="italic">
                These locations are used when messages pertain to a certain geographic area, such as a neighborhood, workplace, or town. We
                encourage you to include home, work, and other locations you might want to hear information about. These address locations
                will not be used for mailing purposes.
            </Text>
            <Divider />
            <AddressDetails />
            {showAddForm && <AddLocationForm onClose={() => setShowAddForm(false)} />}
            <Button
                w="min-content"
                variant="default"
                leftSection={<IconPlus size={16} />}
                disabled={(addresses?.length ?? 0) > 4}
                onClick={() => setShowAddForm(true)}
            >
                Add Address
            </Button>
        </Stack>
    );
};
